import React, { useState, useCallback } from "react";
import styled from "styled-components";
import portfolio from "../content/portfolio.json";

const { certifications } = portfolio;

const Panel = styled.div`
    margin-bottom: 20px;
    text-align: left;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const BadgeGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    @media (max-width: 768px) {
        justify-content: center;
    }
`;

const BadgeWrapper = styled.div`
    position: relative;
    display: inline-flex;
`;

const tileStyles = `
    width: 36px;
    height: 36px;
    border-radius: 5px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f0f6fc;
    border: 1px solid #c8dff5;
    padding: 0;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    text-decoration: none;
    box-sizing: border-box;

    &:hover,
    &:focus-visible {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 120, 215, 0.2);
        outline: none;
    }

    &:focus-visible {
        box-shadow: 0 0 0 2px #fff, 0 0 0 4px #0078d7;
    }
`;

const BadgeButton = styled.button`
    ${tileStyles}
`;

const BadgeLink = styled.a`
    ${tileStyles}
`;

const TileLogo = styled.img`
    width: 28px;
    height: 28px;
    object-fit: contain;
`;

const TileLabel = styled.span`
    color: #0078d7;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    text-align: center;
    line-height: 1.1;
    padding: 2px;
`;

const Tooltip = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    min-width: 160px;
    max-width: 220px;
    padding: 8px 10px;
    background: #333;
    color: #fff;
    border-radius: 6px;
    font-size: 0.75rem;
    line-height: 1.4;
    text-align: center;
    pointer-events: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &::after {
        content: "";
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #333;
    }
`;

const TooltipName = styled.div`
    font-weight: 600;
    margin-bottom: 4px;
`;

const TooltipMeta = styled.div`
    color: #ccc;
    font-size: 0.7rem;
`;

const formatIssued = (issued) => {
    if (!issued) return "";
    const [year, month] = issued.split("-");
    const date = new Date(Number(year), Number(month) - 1);
    return date.toLocaleDateString("en-NZ", { month: "short", year: "numeric" });
};

const formatExpiry = (expires) => {
    if (!expires) return null;
    const [year] = expires.split("-");
    return `Valid until ${year}`;
};

const getBadgeLabel = (cert, provider) => {
    if (cert.shortName) return cert.shortName;
    return provider.slice(0, 4).toUpperCase();
};

const buildMeta = (cert, provider) => {
    const parts = [provider, formatIssued(cert.issued)].filter(Boolean);
    const expiry = formatExpiry(cert.expires);
    if (expiry) parts.push(expiry);
    return parts.join(" · ");
};

const buildAriaLabel = (cert, provider) =>
    `${cert.name}. ${buildMeta(cert, provider)}`;

const buildTitle = (cert, provider) =>
    `${cert.name} — ${buildMeta(cert, provider)}`;

const renderTileContent = (cert, category) => {
    const logo = cert.logo || category.logo;
    if (logo) {
        return <TileLogo src={logo} alt="" />;
    }
    return (
        <TileLabel aria-hidden="true">
            {getBadgeLabel(cert, category.provider)}
        </TileLabel>
    );
};

const CertBadgeTile = ({
    cert,
    category,
    tileKey,
    isActive,
    onShow,
    onHide,
    onToggle,
}) => {
    const provider = category.provider;
    const ariaLabel = buildAriaLabel(cert, provider);
    const title = buildTitle(cert, provider);

    const wrapperProps = {
        onMouseEnter: () => onShow(tileKey),
        onMouseLeave: () => onHide(tileKey),
        onFocus: () => onShow(tileKey),
        onBlur: () => onHide(tileKey),
    };

    if (cert.url) {
        return (
            <BadgeWrapper {...wrapperProps}>
                <BadgeLink
                    href={cert.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={ariaLabel}
                    title={title}
                >
                    {renderTileContent(cert, category)}
                </BadgeLink>
                {isActive && (
                    <Tooltip role="tooltip">
                        <TooltipName>{cert.name}</TooltipName>
                        <TooltipMeta>{buildMeta(cert, provider)}</TooltipMeta>
                    </Tooltip>
                )}
            </BadgeWrapper>
        );
    }

    return (
        <BadgeWrapper {...wrapperProps}>
            <BadgeButton
                type="button"
                aria-label={ariaLabel}
                title={title}
                aria-expanded={isActive}
                onClick={() => onToggle(tileKey)}
            >
                {renderTileContent(cert, category)}
            </BadgeButton>
            {isActive && (
                <Tooltip role="tooltip">
                    <TooltipName>{cert.name}</TooltipName>
                    <TooltipMeta>{buildMeta(cert, provider)}</TooltipMeta>
                </Tooltip>
            )}
        </BadgeWrapper>
    );
};

const getCategories = () => {
    if (certifications.categories) return certifications.categories;

    if (certifications.items) {
        const grouped = certifications.items.reduce((acc, cert) => {
            const key = cert.issuer || "Other";
            if (!acc[key]) {
                acc[key] = {
                    provider: key,
                    logo: cert.logo,
                    logoAlt: cert.logoAlt || key,
                    items: [],
                };
            }
            acc[key].items.push(cert);
            return acc;
        }, {});
        return Object.values(grouped);
    }

    return [];
};

const flattenCategories = (categories) =>
    categories.flatMap((category) =>
        category.items.map((cert) => ({ cert, category }))
    );

const CertificationsPanel = () => {
    const badges = flattenCategories(getCategories());
    const [activeCert, setActiveCert] = useState(null);

    const showTooltip = useCallback((key) => {
        setActiveCert(key);
    }, []);

    const hideTooltip = useCallback((key) => {
        setActiveCert((current) => (current === key ? null : current));
    }, []);

    const toggleTooltip = useCallback((key) => {
        setActiveCert((current) => (current === key ? null : key));
    }, []);

    return (
        <Panel>
            <BadgeGrid aria-label="Licenses and certifications">
                {badges.map(({ cert, category }) => {
                    const tileKey = `${category.provider}-${cert.name}`;
                    return (
                        <CertBadgeTile
                            key={tileKey}
                            cert={cert}
                            category={category}
                            tileKey={tileKey}
                            isActive={activeCert === tileKey}
                            onShow={showTooltip}
                            onHide={hideTooltip}
                            onToggle={toggleTooltip}
                        />
                    );
                })}
            </BadgeGrid>
        </Panel>
    );
};

export default CertificationsPanel;
